import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    ErrorOutline as ErrorOutlineIcon,
    Paid as PaidIcon,
    SyncAlt as SyncAltIcon,
    Forum as ForumIcon,
    EmojiEmotions as EmojiEmotionsIcon,
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    Close as CloseIcon,
    MenuBook as MenuBookIcon,
    ArrowForward as ArrowForwardIcon,
    TipsAndUpdates as TipsAndUpdatesIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに：ルームシェアの魅力と現実' },
    { id: 'common-troubles', label: 'よくあるルームシェアトラブル事例' },
    { id: 'money-trouble', label: '金銭トラブルの原因と解決法' },
    { id: 'life-habit', label: '生活習慣の摩擦への対処術' },
    { id: 'communication', label: 'コミュニケーション不足をどう乗り越えるか' },
    { id: 'resolution-tips', label: '円満解決のコツと実践ポイント' },
    { id: 'conclusion', label: 'まとめとこれからルームシェアする人へのアドバイス' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                目次
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowToc(false)}
                                startIcon={<CloseIcon />}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem
                                    key={item.id}
                                    sx={{
                                        p: 0.5,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#555',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            fontSize: '1.1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{
                                            cursor: 'pointer',
                                            fontSize: '0.97rem',
                                            textAlign: 'left',
                                            p: 0,
                                            minWidth: 0
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<MenuBookIcon />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに：ルームシェアの魅力と現実
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    近年、住まいの選択肢として注目を集めている「ルームシェア」。家賃の節約や、広い住空間を実現できるだけでなく、多様な人との出会いや価値観の交流も大きな魅力です。
                    しかし、実際にルームシェアを始めてみると、予想外のトラブルや小さなストレスが積み重なり、せっかくの新生活がギクシャクしてしまうケースも少なくありません。
                </Typography>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    
                </Box>
                <Typography variant="body1">
                    本記事では、ルームシェアで実際によくあるトラブル事例とその解決法を、実体験や豊富な事例をもとに徹底解説します。
                    あなたのルームシェア生活をより快適に、そしてトラブルを未然に防ぐための「対処術」を、わかりやすくお届けします。
                </Typography>
            </Box>

            {/* よくあるトラブル事例 */}
            <Box id="common-troubles" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくあるルームシェアトラブル事例
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア経験者からよく聞く、代表的なトラブルのパターンを紹介します。これらは誰にでも起こり得る問題です。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <PaidIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="家賃・光熱費など金銭トラブル"
                            secondary="支払の遅延や不公平感、清算ミスなどが発生しやすい"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <SyncAltIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="生活リズム・習慣の摩擦"
                            secondary="就寝・起床時間、清掃頻度、騒音、共有スペースの使い方など"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <ForumIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                            primary="コミュニケーション不足からの誤解"
                            secondary="些細なすれ違いや、言い出せずにストレスが膨らむケース"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <ErrorOutlineIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                            primary="共有物の破損・紛失"
                            secondary="誰の責任か分からずトラブルに"
                        />
                    </ListItem>
                </List>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                実体験エピソード
                            </Typography>
                            <Typography variant="body2">
                                「友人同士で始めたルームシェア。最初の数ヶ月は問題なかったが、家賃の精算を曖昧にしていたため、1年後に未払い分が発覚し険悪な空気に。お金の話を曖昧にせず、最初にルール化しておけば良かったと痛感しました。」
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: '#f2f6fc' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                チェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="お金・ルール・家事分担は最初に明文化" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="共有物・共有スペースの利用ルールを作る" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 金銭トラブル */}
            <Box id="money-trouble" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    金銭トラブルの原因と解決法
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで最も発生しやすいのが金銭問題です。友人同士でも、金銭感覚や支払い意識の差が顕著に現れます。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <PaidIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="家賃・光熱費の支払い遅延"
                            secondary="支払い忘れやタイミングのズレが積もって不満に"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <PaidIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="割り勘・精算の不公平感"
                            secondary="家事や消耗品の負担に差が出る"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    
                </Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    【解決法：お金は「見える化」と「自動化」がカギ】
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="家賃・光熱費は自動引き落としや専用アプリで管理"
                            secondary="共有口座やアプリ（LINE Pay, Splitwiseなど）活用でトラブル予防"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="ルールは最初に明文化し、定期的に見直す"
                            secondary="曖昧なままにせず、月1回の精算日を設けて話し合う"
                        />
                    </ListItem>
                </List>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                専門家のアドバイス
                            </Typography>
                            <Typography variant="body2">
                                「お金の話は気まずいものですが、最初に徹底しておくことで後々のストレスを激減できます。支払いの自動化やルールの可視化が、信頼関係維持の秘訣です。」
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#f2f6fc' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                トラブル回避のチェックリスト
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="分担額・支払日を紙やアプリで明示" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="遅延時のペナルティも事前合意" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 生活習慣の摩擦 */}
            <Box id="life-habit" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    生活習慣の摩擦への対処術
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    生活リズムや価値観の違いは、最初は些細でも次第に大きなトラブルに発展しがちです。特に気をつけたいポイントは以下の通り。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <SyncAltIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="就寝・起床時間の違い"
                            secondary="夜型と朝型で音や照明の使い方に不満が"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <HomeIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="掃除や片付けの頻度・基準"
                            secondary="キレイ好きとズボラで摩擦"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <EmojiEmotionsIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                            primary="友人や恋人の招待ルール"
                            secondary="プライバシー感覚の違いがストレスに"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/cleaning-share_roomshare-trouble-cases-and-solutions.jpg"
                        alt="ナチュラルなキッチンでルームメイト2人が掃除と片付けを分担し協力している様子。明るい陽射しの中で和やかに作業し、良好な関係性を象徴している"
                        style={{
                            borderRadius: 10,
                            maxWidth: '100%',
                            boxShadow: '0 2px 14px rgba(120,120,180,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    【解決法：ルーティンと「言い方の工夫」が大事】
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="掃除・ゴミ出しなどは当番制 or ルーティン化"
                            secondary="カレンダーやホワイトボードで見える化"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="違和感や不満は溜め込まず、丁寧に相談"
                            secondary="「お願いベース」で伝える・感謝を言葉に"
                        />
                    </ListItem>
                </List>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#f2f6fc', mt: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                よくあるNGパターン
                            </Typography>
                            <Typography variant="body2">
                                「またゴミ出し忘れてるよ！」と感情的に指摘すると、相手も防衛的になり関係が悪化しがちです。
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                ポイントは「提案型」の伝え方
                            </Typography>
                            <Typography variant="body2">
                                「来週からゴミ当番を交代制にしない？」など、前向きな提案で協力体制を作りましょう。
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* コミュニケーション不足 */}
            <Box id="communication" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    コミュニケーション不足をどう乗り越えるか
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    どんなに気が合うルームメイトでも、話し合いの場を持たないと小さなモヤモヤが溜まりやすいものです。
                    特に、以下のような場面でトラブルが表面化しやすいです。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <ForumIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="「気を遣いすぎて言えない」問題"
                            secondary="遠慮しすぎてストレスが蓄積"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <ForumIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                            primary="LINEやメモだけの伝言"
                            secondary="ニュアンスが伝わらず誤解発生"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/share-meeting_roomshare-trouble-cases-and-solutions.jpg"
                        alt="リビングのソファで4人のルームメイトがカジュアルにミーティングし、笑顔で意見交換している様子。明るい照明とリラックスした雰囲気が、良好なコミュニケーションの大切さを表現"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 14px rgba(120,120,180,0.09)'
                        }}
                    />
                </Box>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    【解決法：定期的な「顔合わせ」と「日常の声かけ」】
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="月1回の「シェア会議」やご飯会を企画"
                            secondary="課題や要望をカジュアルに話し合う場を作る"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="LINEやメモだけでなく、顔を合わせて会話"
                            secondary="ちょっとした相談も直接伝える習慣を"
                        />
                    </ListItem>
                </List>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#f8f9fa', mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ワンポイント
                    </Typography>
                    <Typography variant="body2">
                        トラブルの芽は「伝えられなかった小さな不満」から生まれます。
                        何でも言い合える雰囲気作りが、円満ルームシェアの最大のカギです。
                    </Typography>
                </Paper>
            </Box>

            {/* 円満解決のコツ */}
            <Box id="resolution-tips" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    円満解決のコツと実践ポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでのトラブルを未然に防ぐ、または起きてしまった時に円滑に解決するためのポイントをまとめました。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#f4fbfa' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                トラブルを防ぐ5つの鉄則
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="最初にルール・お金・家事分担を明文化" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="月1回のミーティングで課題を共有" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="困った時は「言い方」に気を配る" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="相手の立場に立って考える姿勢" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="感謝・労いの言葉を惜しまない" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#f2f6fc' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                実践して欲しいチェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="トラブルが起きたら、感情的にならず冷静に事実を整理" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="第三者（管理会社や共通の友人）に相談も有効" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ArrowForwardIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="解決後は「ありがとう」を伝え合う" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* まとめ・アドバイス */}
            <Box id="conclusion" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめとこれからルームシェアする人へのアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、人生の中でも貴重な経験となる可能性を秘めています。
                    トラブルは避けて通れないものですが、きちんと対処すれば人間関係を深め、あなた自身の成長にもつながります。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    この記事で紹介した事例や対処法が、あなたのルームシェア生活をより快適で実りあるものにする一助となれば幸いです。
                    ぜひ、最初の一歩は「ルール作り」と「話し合い」から始めてみてください。
                </Typography>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    <img
                        src="/images/blog/share-success_roomshare-trouble-cases-and-solutions.jpg"
                        alt="ルームシェアの住人たちがリビングで肩を組み、笑顔でカメラに向かってピースサイン。温かみのある照明とカラフルなクッションが和やかな雰囲気を演出し、協力的な関係性の象徴となっている"
                        style={{
                            borderRadius: 12,
                            maxWidth: '100%',
                            boxShadow: '0 2px 14px rgba(130,120,180,0.10)'
                        }}
                    />
                </Box>
                <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ルームシェア生活のキーワード
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip label="ルール作り" color="primary" variant="outlined" />
                        <Chip label="コミュニケーション" color="primary" variant="outlined" />
                        <Chip label="金銭管理" color="primary" variant="outlined" />
                        <Chip label="感謝" color="primary" variant="outlined" />
                        <Chip label="協力" color="primary" variant="outlined" />
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-trouble-cases-and-solutions',
    slug: 'roomshare-trouble-cases-and-solutions',
    title: 'ルームシェアのトラブル事例と解決法：実体験から学ぶ対処術',
    excerpt: 'よくあるトラブル事例\n金銭トラブル解決法\n生活習慣の摩擦対応\nコミュニケーション不足問題\n円満解決のコツ',
    coverImage: '/images/blog/cover-roomshare-trouble_roomshare-trouble-cases-and-solutions.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木製のテーブルや観葉植物、カラフルなクッションなどが温かみを感じさせ、住人同士の協力的な雰囲気を伝えている',
    publishedAt: '2024-06-04T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', 'トラブル対策', '暮らしの知恵'],
    tags: ['ルームシェア', 'トラブル事例', '解決法', '金銭管理', 'コミュニケーション'],
    author: {
        name: '高橋 彩',
        avatar: '/images/avatars/takahashi-aya.jpg',
        bio: 'ルームシェア歴10年以上・住まいと人間関係の専門家。数多くのシェアハウス運営や相談経験をもとに、トラブル予防の実践ノウハウを発信中。',
        profilePicture: '/images/avatars/takahashi-aya.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;